import type { ScheduleItem } from '../../domain/entities/schedule.ts';
import type { Job } from '../../domain/entities/job.ts';
import { addWorkingDays, nextWorkingDay, type WorkCalendar } from '../../domain/work-calendar.ts';
import { deleteWrite, expectUnchanged, updateWrite, type BatchWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import { cascadeSchedule, type ScheduleShift } from './dependencies.ts';
import {
  applyItemPatch,
  listScheduleItems,
  parseScheduleItemInput,
  resolveSpan,
  UpdateScheduleItemInputSchema,
  type UpdateScheduleItemInput,
} from './save-schedule-item.ts';
import {
  ShiftReasonRequiredError,
  withRetry,
  type SchedulePlanContext,
  type ShiftIntent,
} from './schedule-plan.ts';
import { requiresReason, toShiftRecord, withShift } from './shifts.ts';
import { loadJobCalendar } from './workday-exceptions.ts';

/**
 * Acting on several items at once.
 *
 * Every one of these is a single batch, for the same reason one item's move
 * is: half a bulk action is worse than none. Somebody who shifted twelve
 * items and got seven has a schedule that is wrong in a way they cannot see,
 * and no way to tell which five to redo.
 *
 * They are separate from the single-item use cases rather than loops over
 * them, because a loop would be N commits and N cascades — and the
 * intermediate states between those commits are schedules that contradict
 * themselves.
 */
export class BulkScheduleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BulkScheduleError';
  }
}

/** The most items one action may touch. */
export const MAX_BULK_ITEMS = 200;

export interface BulkResult {
  /** Items the action was asked to change. */
  readonly changed: number;
  /** Every date that moved, including ones only pushed by a dependency. */
  readonly shifts: readonly ScheduleShift[];
}

function assertSelection(itemIds: readonly string[]): Set<string> {
  if (itemIds.length === 0) throw new BulkScheduleError('Nothing was selected');
  if (itemIds.length > MAX_BULK_ITEMS) {
    // Refused rather than split, like `DataPort.commit`: a bulk action that
    // quietly became two is exactly the half-applied state this avoids.
    throw new BulkScheduleError(
      `That is more than ${MAX_BULK_ITEMS} items. Select fewer and repeat the action.`,
    );
  }
  return new Set(itemIds);
}

/** The ones named that are really there, or an error naming how many are not. */
function selectedFrom(items: readonly ScheduleItem[], ids: Set<string>): ScheduleItem[] {
  const found = items.filter((item) => ids.has(item.id));
  if (found.length !== ids.size) {
    // Somebody else deleted one while this selection was on screen. Acting on
    // the rest would half-apply the thing this function exists to prevent.
    throw new NotFoundError('scheduleItems', `${ids.size - found.length} of the selected items`);
  }
  return found;
}

/**
 * Whether any of these moves needs explaining, and refuse if so.
 *
 * Asked once for the whole action rather than per item: a bulk shift is one
 * decision, so one reason covers it. Checking per item would prompt twelve
 * times for one gesture.
 */
async function assertReasonGiven(
  context: SchedulePlanContext,
  moved: readonly ScheduleItem[],
  attribution: ShiftIntent,
): Promise<void> {
  if (attribution.reason || moved.length === 0) return;
  const job = await context.data
    .collection<Job>({ companyId: context.companyId }, 'jobs')
    .get(context.jobId);
  if (!job) return;
  const published = moved.find((item) => requiresReason(item, job));
  if (published) throw new ShiftReasonRequiredError(published.title);
}

/**
 * Write the whole settled schedule, recording what moved.
 *
 * Shared by the bulk operations because they differ only in how they compute
 * the proposal. `directIds` says which items the person acted on: everything
 * else moved because something it depends on did, and calling that a replan
 * sends people after the wrong work.
 */
function bulkWrites(
  context: SchedulePlanContext,
  before: readonly ScheduleItem[],
  after: readonly ScheduleItem[],
  now: string,
  directIds: ReadonlySet<string>,
  attribution: ShiftIntent,
  details?: ReadonlyMap<string, Partial<ScheduleItem>>,
): { writes: BatchWrite[]; shifts: ScheduleShift[] } {
  const previous = new Map(before.map((item) => [item.id, item]));
  const scope = { companyId: context.companyId, jobId: context.jobId };
  const writes: BatchWrite[] = [];
  const shifts: ScheduleShift[] = [];

  for (const item of after) {
    const existing = previous.get(item.id)!;
    const datesMoved = item.startDate !== existing.startDate || item.endDate !== existing.endDate;
    const linksChanged =
      JSON.stringify(item.predecessors) !== JSON.stringify(existing.predecessors);
    const patch = details?.get(item.id);
    if (!datesMoved && !linksChanged && patch === undefined) continue;

    if (datesMoved) {
      shifts.push({
        itemId: item.id,
        fromStart: existing.startDate,
        toStart: item.startDate,
        fromEnd: existing.endDate,
        toEnd: item.endDate,
      });
    }

    const history = datesMoved
      ? withShift(
          existing,
          toShiftRecord(
            {
              itemId: item.id,
              fromStart: existing.startDate,
              toStart: item.startDate,
              fromEnd: existing.endDate,
              toEnd: item.endDate,
            },
            {
              at: now,
              byUid: context.uid,
              cascade: !directIds.has(item.id),
              reason: attribution.reason,
              note: attribution.note,
            },
          ),
        )
      : existing.shifts;

    writes.push(
      updateWrite<ScheduleItem>(
        scope,
        'scheduleItems',
        item.id,
        {
          ...(patch ?? {}),
          startDate: item.startDate,
          endDate: item.endDate,
          workDays: item.workDays,
          predecessors: item.predecessors,
          shifts: history,
          updatedAt: now,
          updatedBy: context.uid,
        },
        expectUnchanged(existing),
      ),
    );
  }

  return { writes, shifts };
}

/**
 * Where a set of items lands when moved by so many working days.
 *
 * Extracted so the **preview and the write cannot disagree**. The
 * assistant proposes a shift before anybody confirms it, and a preview
 * computed separately would eventually say one thing and do another —
 * which is worse than no preview, because somebody confirmed it.
 *
 * Pure, and before the cascade: the caller passes the result through
 * `cascadeSchedule`, which is what moves the work that depends on this.
 */
export function shiftedBy(
  calendar: WorkCalendar,
  items: readonly ScheduleItem[],
  ids: ReadonlySet<string>,
  byWorkingDays: number,
): ScheduleItem[] {
  return items.map((item) => {
    if (!ids.has(item.id)) return item;
    const from = nextWorkingDay(calendar, item.startDate);
    const span = resolveSpan(calendar, {
      startDate: addWorkingDays(calendar, from, byWorkingDays),
      workDays: item.workDays,
    });
    return { ...item, ...span };
  });
}

/**
 * Move several items by the same number of **working days**.
 *
 * Working days, like every other duration here: "push it a week" means five
 * working days, and shifting by seven calendar days would land half a
 * selection on a weekend and roll it forward unevenly.
 *
 * Durations are kept. A bulk shift moves work, it does not resize it.
 */
export async function shiftScheduleItems(
  context: SchedulePlanContext,
  itemIds: readonly string[],
  byWorkingDays: number,
  attribution: ShiftIntent = {},
): Promise<BulkResult> {
  const ids = assertSelection(itemIds);
  if (!Number.isInteger(byWorkingDays)) {
    throw new BulkScheduleError('Shift by a whole number of work days');
  }
  if (byWorkingDays === 0) throw new BulkScheduleError('That would not move anything');

  return withRetry(async () => {
    const items = await listScheduleItems(context);
    const selected = selectedFrom(items, ids);
    const calendar = await loadJobCalendar(context);
    await assertReasonGiven(context, selected, attribution);

    const now = context.clock.now().toISOString();
    const settled = cascadeSchedule(calendar, shiftedBy(calendar, items, ids, byWorkingDays));
    const { writes, shifts } = bulkWrites(context, items, settled.items, now, ids, attribution);
    if (writes.length > 0) await context.data.commit(writes);
    return { changed: selected.length, shifts };
  });
}

/**
 * Apply the same change to several items.
 *
 * For the changes a bulk action is actually for — a colour, a phase, a
 * reminder, marking work done. Dates are not accepted: moving a selection
 * means shifting each item from where *it* is, which is
 * `shiftScheduleItems`, not setting them all to one date.
 */
export type BulkPatch = Pick<
  UpdateScheduleItemInput,
  'colour' | 'phaseId' | 'completed' | 'progress' | 'reminder' | 'assignees' | 'visibility'
>;

export async function updateScheduleItems(
  context: SchedulePlanContext,
  itemIds: readonly string[],
  patch: BulkPatch,
): Promise<BulkResult> {
  const ids = assertSelection(itemIds);
  const parsed = parseScheduleItemInput(UpdateScheduleItemInputSchema, patch);
  if (Object.keys(parsed).length === 0) throw new BulkScheduleError('Nothing was changed');

  return withRetry(async () => {
    const items = await listScheduleItems(context);
    const selected = selectedFrom(items, ids);
    const calendar = await loadJobCalendar(context);
    const now = context.clock.now().toISOString();

    const details = new Map<string, Partial<ScheduleItem>>();
    const proposed = items.map((item) => {
      if (!ids.has(item.id)) return item;
      const span = {
        startDate: item.startDate,
        workDays: item.workDays,
        endDate: item.endDate,
      };
      const next = applyItemPatch(item, parsed, span, { at: now, uid: context.uid });
      const {
        id: _id,
        startDate: _start,
        endDate: _end,
        workDays: _days,
        predecessors: _links,
        shifts: _shifts,
        createdAt: _createdAt,
        createdBy: _createdBy,
        ...rest
      } = next;
      details.set(item.id, rest);
      return next;
    });

    // Cascaded anyway: none of these fields moves a date, but a phase change
    // can reorder the view, and settling from the stored state costs nothing
    // and cannot be forgotten later.
    const settled = cascadeSchedule(calendar, proposed);
    const { writes, shifts } = bulkWrites(context, items, settled.items, now, ids, {}, details);
    if (writes.length > 0) await context.data.commit(writes);
    return { changed: selected.length, shifts };
  });
}

/**
 * Delete several items, unlinking everything that depended on them.
 *
 * The unlinking is the half that matters. A predecessor pointing at an item
 * that no longer exists makes the whole schedule uncomputable — every
 * cascade, the critical path and the re-settle walk the graph and throw on a
 * reference they cannot resolve. Deleting twelve items without this would
 * disable the schedule twelve times over.
 *
 * Dates are deliberately *not* re-settled afterwards. Work does not come
 * forward because its predecessor was deleted: those dates have been given
 * to people, and pulling them earlier silently is not a decision anybody
 * made.
 */
export async function deleteScheduleItems(
  context: SchedulePlanContext,
  itemIds: readonly string[],
): Promise<BulkResult> {
  const ids = assertSelection(itemIds);

  return withRetry(async () => {
    const items = await listScheduleItems(context);
    const selected = selectedFrom(items, ids);
    const now = context.clock.now().toISOString();
    const scope = { companyId: context.companyId, jobId: context.jobId };

    const writes: BatchWrite[] = selected.map((item) =>
      deleteWrite(scope, 'scheduleItems', item.id),
    );

    for (const item of items) {
      if (ids.has(item.id)) continue;
      const kept = item.predecessors.filter((predecessor) => !ids.has(predecessor.itemId));
      if (kept.length === item.predecessors.length) continue;
      writes.push(
        updateWrite<ScheduleItem>(
          scope,
          'scheduleItems',
          item.id,
          { predecessors: kept, updatedAt: now, updatedBy: context.uid },
          expectUnchanged(item),
        ),
      );
    }

    await context.data.commit(writes);
    return { changed: selected.length, shifts: [] };
  });
}

/** One person to put on, or take off, a set of items. */
export interface AssigneeRef {
  readonly kind: 'user' | 'contact';
  readonly id: string;
}

/**
 * Add or remove people across a selection, merging per item.
 *
 * Deliberately not `updateScheduleItems({ assignees })`. That applies one
 * value to every item, so "put the electrician on these twelve" would
 * replace whoever else was already on them — silently, and across twelve
 * rows at once. What somebody means by a bulk assign is a change to the
 * set, not a new set.
 *
 * `notify` is true for anyone added, for the same reason the item form
 * defaults it: somebody put on an item is being told about it, and adding
 * them silently is the same as not adding them. Anyone already on an item
 * keeps the `notify` they had rather than being reset.
 */
export async function assignScheduleItems(
  context: SchedulePlanContext,
  itemIds: readonly string[],
  change: { readonly add?: readonly AssigneeRef[]; readonly remove?: readonly AssigneeRef[] },
): Promise<BulkResult> {
  const ids = assertSelection(itemIds);
  const add = change.add ?? [];
  const remove = change.remove ?? [];
  if (add.length === 0 && remove.length === 0) {
    throw new BulkScheduleError('Nobody was added or removed');
  }

  const key = (assignee: AssigneeRef) => `${assignee.kind}:${assignee.id}`;
  const removing = new Set(remove.map(key));
  if (add.some((assignee) => removing.has(key(assignee)))) {
    // Otherwise the answer depends on which of the two is applied last,
    // which is not something a caller should have to know.
    throw new BulkScheduleError('Somebody is being both added and removed');
  }

  return withRetry(async () => {
    const items = await listScheduleItems(context);
    const selected = selectedFrom(items, ids);
    const now = context.clock.now().toISOString();
    const scope = { companyId: context.companyId, jobId: context.jobId };
    const writes: BatchWrite[] = [];

    for (const item of selected) {
      const kept = item.assignees.filter((assignee) => !removing.has(key(assignee)));
      const present = new Set(kept.map(key));
      const next = [
        ...kept,
        ...add
          .filter((assignee) => !present.has(key(assignee)))
          .map((assignee) => ({ ...assignee, notify: true })),
      ];

      // Unchanged items are skipped, so adding somebody who is already on
      // half the selection does not stamp `updatedAt` across the other half.
      if (JSON.stringify(next) === JSON.stringify(item.assignees)) continue;

      writes.push(
        updateWrite<ScheduleItem>(
          scope,
          'scheduleItems',
          item.id,
          { assignees: next, updatedAt: now, updatedBy: context.uid },
          expectUnchanged(item),
        ),
      );
    }

    if (writes.length > 0) await context.data.commit(writes);
    // What was asked for, not what changed: "12 items" is the selection the
    // person made, and reporting 7 because 5 already had that sub reads as
    // a failure.
    return { changed: selected.length, shifts: [] };
  });
}
